// src/hooks/useFreteCalculator.js
import { useState, useCallback } from "react";
import { useApi } from "./useApi";

/**
 * Hook para calcular frete de entrega
 * @returns {Object} { freight, loading, error, calculate, clearFreight }
 */
export function useFreteCalculator() {
  const api = useApi();
  const [freight, setFreight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Calcula o frete para um CEP específico
   * @param {string} cep - CEP de destino (apenas dígitos)
   * @returns {Promise<Object|null>} Dados do frete ou null em caso de erro
   */
  const calculate = useCallback(
    async (cep) => {
      // Remove caracteres não numéricos
      const cleanCep = String(cep || "").replace(/\D/g, "");

      if (cleanCep.length !== 8) {
        setError("CEP deve conter 8 dígitos");
        setFreight(null);
        return null;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`🚚 Calculando frete para CEP: ${cleanCep}`);

        const data = await api.get(`/frete/${cleanCep}/calcular`);

        console.log("📦 Resposta do cálculo de frete:", data);

        // Estrutura esperada da resposta:
        // {
        //   originCep: "04658000",
        //   destinationCep: "04658000",
        //   distanceKm: 0.50,
        //   freightValue: 2.00,
        //   available: true,
        //   message: "Frete disponível"
        // }

        const freightData = {
          originCep: data.originCep,
          destinationCep: data.destinationCep,
          distanceKm: Number(data.distanceKm || 0),
          value: Number(data.freightValue || 0),
          available: Boolean(data.available),
          message: data.message || "",
          calculationId: Number(data.distanceKm || 0), // freightCalculationId = distanceKm conforme solicitado
          // CEP (só dígitos) usado no cálculo: o checkout descarta o frete se o CEP mudar
          requestedCep: cleanCep,
        };

        setFreight(freightData);

        return freightData;
      } catch (err) {
        console.error("❌ Erro ao calcular frete:", err);
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Não foi possível calcular o frete";
        setError(errorMessage);
        setFreight(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Limpa os dados de frete
   */
  const clearFreight = useCallback(() => {
    setFreight(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    freight,
    loading,
    error,
    calculate,
    clearFreight,
  };
}
